/**
 * Demo Center deploy card (WQ-338) — one component, two placements:
 * CloudOps ("IWT Demo Center") and Demo Center → Manage.
 *
 * Renders nothing unless /status says `can_publish: true`. Deploy and Roll back
 * ask for confirmation; a non-JSON answer to a mutation shows "Request was
 * blocked", never success. While a run is queued or in progress the card polls
 * every 10 s and stops on a terminal state.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDemoStatus } from '../../hooks/useDemoStatus';
import type { DemoComponent } from '../../services/demoStatusService';
import { resolveIcon } from '../common/IconResolver';
import {
  addPublisher,
  getPublishHistory,
  getPublishStatus,
  isActiveRun,
  listPublishers,
  removePublisher,
  rollbackPublish,
  triggerPublish,
  type PublishResult,
  type PublishRun,
  type PublishStatus,
  type Publisher,
} from '../../services/demoPublishService';

export const POLL_MS = 10_000;

type Phase = 'loading' | 'hidden' | 'unconfigured' | 'error' | 'ready';
type Notice = { tone: 'info' | 'error'; text: string } | null;
type Confirm = { kind: 'deploy' } | { kind: 'rollback'; sha: string } | null;

const short = (sha: string | null | undefined) => (sha && /^[0-9a-f]{40}$/.test(sha) ? sha.slice(0, 7) : sha || '—');
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');

const ERROR_TEXT: Record<string, string> = {
  deploy_in_progress: 'A deployment is already running.',
  sha_not_a_recent_successful_deploy: 'That version is not one of the last 10 successful deployments.',
  invalid_sha: 'That version is not a valid commit.',
  user_not_found: 'No user has that email address.',
  user_not_entitled: 'That user is not in the IWT organization.',
  user_is_super_admin: 'Super admins can always deploy.',
  pat_invalid: 'The GitHub token was rejected.',
  github_unreachable: 'GitHub could not be reached.',
};

function describe(result: PublishResult<unknown>, success: string): Notice {
  if (result.kind === 'ok') return { tone: 'info', text: success };
  if (result.kind === 'blocked') return { tone: 'error', text: 'Request was blocked.' };
  if (result.kind === 'error') return { tone: 'error', text: ERROR_TEXT[result.error] ?? `Request failed (${result.error}).` };
  return { tone: 'error', text: 'Deploying is not configured yet.' };
}

const RunBadge: React.FC<{ run: PublishRun }> = ({ run }) => {
  const label = run.conclusion || run.status || 'unknown';
  const cls = run.conclusion === 'success' ? 'badge-success' : run.conclusion ? 'badge-error' : 'badge-warning';
  return <span className={`badge ${cls}`}>{label.replace('_', ' ')}</span>;
};

const ConfirmDialog: React.FC<{
  title: string; body: string; confirmLabel: string; onCancel: () => void; onConfirm: () => void;
}> = ({ title, body, confirmLabel, onCancel, onConfirm }) => createPortal(
  <div
    onClick={onCancel}
    style={{
      position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)', padding: 16,
    }}
  >
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%', maxWidth: 440, background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur-lg)',
        border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-2xl, 16px)', boxShadow: 'var(--glass-shadow-lg)',
      }}
    >
      <div style={{ padding: '20px 24px 8px' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</h3>
      </div>
      <p style={{ margin: 0, padding: '8px 24px 16px', fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{body}</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '0 24px 20px' }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </div>
  </div>,
  document.body,
);

const Publishers: React.FC<{
  publishers: Publisher[]; onAdd: (email: string) => Promise<void>; onRemove: (userId: string) => void; busy: boolean;
}> = ({ publishers, onAdd, onRemove, busy }) => {
  const [email, setEmail] = useState('');
  return (
    <div data-testid="demo-publishers" style={{ marginTop: 'var(--space-5, 20px)' }}>
      <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Publishers</h4>
      <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-secondary)' }}>
        IWT users who may deploy. Super admins always can.
      </p>
      {publishers.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No publishers yet.</div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {publishers.map((p) => (
            <li key={p.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, borderBottom: '1px solid var(--border-secondary, rgba(148,163,184,0.15))' }}>
              <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', color: 'var(--text-primary)' }}>{p.email}</span>
              {p.granted_by && <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>by {p.granted_by}</span>}
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onRemove(p.user_id)} aria-label={`Remove ${p.email}`}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        onSubmit={async (e) => { e.preventDefault(); if (email.trim()) { await onAdd(email.trim()); setEmail(''); } }}
        style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}
      >
        <input
          type="email"
          className="input"
          aria-label="Publisher email"
          placeholder="name@iwtwireless.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ flex: '1 1 220px', minWidth: 0, padding: '6px 10px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--glass-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
        />
        <button type="submit" className="btn btn-secondary btn-sm" disabled={busy || !email.trim()}>Add</button>
      </form>
    </div>
  );
};

const DemoPublishCard: React.FC<{ title: string; components?: DemoComponent[] }> = ({ title, components }) => {
  const { authFetch } = useAuth();
  // WQ-348: the Demo Center page passes its components; elsewhere (CloudOps) the card polls itself.
  const own = useDemoStatus(authFetch, { enabled: components === undefined });
  const liveComponents = components ?? (own.result?.kind === 'ok' ? own.result.status.components : []);
  const componentVersions = liveComponents.filter((c) => c.id !== 'host' && c.version);
  const [phase, setPhase] = useState<Phase>('loading');
  const [status, setStatus] = useState<PublishStatus | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [history, setHistory] = useState<PublishRun[]>([]);
  const [publishers, setPublishers] = useState<Publisher[] | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  const [awaitingRunAfter, setAwaitingRunAfter] = useState<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const loadPublishers = useCallback(async () => {
    const result = await listPublishers(authFetch);
    if (mounted.current && result.kind === 'ok') setPublishers(result.data.publishers);
  }, [authFetch]);

  const refresh = useCallback(async (withHistory = true) => {
    const result = await getPublishStatus(authFetch);
    if (!mounted.current) return;
    if (result.kind === 'hidden') return setPhase('hidden');
    if (result.kind === 'unconfigured') {
      setIsSuperAdmin(result.isSuperAdmin);
      setPhase('unconfigured');
      if (result.isSuperAdmin) loadPublishers();
      return;
    }
    if (result.kind !== 'ok') return setPhase('error');
    setStatus(result.data);
    setIsSuperAdmin(result.data.is_super_admin);
    setPhase('ready');
    if (withHistory) {
      const runs = await getPublishHistory(authFetch);
      if (mounted.current && runs.kind === 'ok') setHistory(runs.data.runs);
    }
  }, [authFetch, loadPublishers]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (isSuperAdmin && phase === 'ready' && publishers === null) loadPublishers(); }, [isSuperAdmin, phase, publishers, loadPublishers]);

  const lastRun = status?.last_run ?? null;
  const active = isActiveRun(lastRun);
  // After a dispatch, keep polling until a newer run shows up (GitHub returns no run id).
  const waiting = awaitingRunAfter !== null;
  useEffect(() => {
    if (awaitingRunAfter !== null && lastRun && lastRun.id !== awaitingRunAfter) setAwaitingRunAfter(null);
  }, [awaitingRunAfter, lastRun]);
  useEffect(() => {
    if (phase !== 'ready' || !(active || waiting)) return undefined;
    const timer = setTimeout(() => { refresh(); }, POLL_MS);
    return () => clearTimeout(timer);
  }, [phase, active, waiting, status, refresh]);

  const runMutation = async (result: Promise<PublishResult<unknown>>, success: string, afterDispatch: boolean) => {
    setBusy(true);
    setNotice(null);
    const outcome = await result;
    if (!mounted.current) return;
    setBusy(false);
    if (outcome.kind === 'unconfigured') return setPhase('unconfigured');
    setNotice(describe(outcome, success));
    if (outcome.kind === 'ok' && afterDispatch) {
      setAwaitingRunAfter(lastRun?.id ?? -1);
      refresh();
    }
  };

  const onConfirm = () => {
    const current = confirm;
    setConfirm(null);
    if (current?.kind === 'deploy') runMutation(triggerPublish(authFetch), 'Deploy started.', true);
    if (current?.kind === 'rollback') runMutation(rollbackPublish(authFetch, current.sha), `Rollback to ${short(current.sha)} started.`, true);
  };

  const onAddPublisher = async (email: string) => {
    setBusy(true);
    const result = await addPublisher(authFetch, email);
    if (!mounted.current) return;
    setBusy(false);
    setNotice(describe(result, `${email} can now deploy.`));
    if (result.kind === 'ok') setPublishers(result.data.publishers);
  };

  const onRemovePublisher = async (userId: string) => {
    setBusy(true);
    const result = await removePublisher(authFetch, userId);
    if (!mounted.current) return;
    setBusy(false);
    setNotice(describe(result, 'Publisher removed.'));
    if (result.kind === 'ok') setPublishers(result.data.publishers);
  };

  if (phase === 'hidden') return null;

  const deployed = status?.deployed ?? null;
  const upToDate = !!deployed && !!status?.latest_master_sha && deployed.sha === status.latest_master_sha;

  return (
    <section className="card" data-testid="demo-publish-card" aria-label={title} style={{ transform: 'none', marginTop: 'var(--space-6, 24px)' }}>
      <h3 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
        {resolveIcon('upload-cloud', 18)} {title}
      </h3>

      {phase === 'loading' && <div role="status" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading deployment status…</div>}

      {phase === 'error' && (
        <div data-testid="demo-publish-error" style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
          Deployment status is unavailable.
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setPhase('loading'); refresh(); }}>Retry</button>
        </div>
      )}

      {phase === 'unconfigured' && (
        <div data-testid="demo-publish-unconfigured" style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Deploying is not configured yet: the demos GitHub token is missing
          (Secrets Manager <code>platform/integrations/github-demos</code>).
        </div>
      )}

      {notice && (
        <div role={notice.tone === 'error' ? 'alert' : 'status'} className={notice.tone === 'error' ? 'alert alert-error' : 'alert alert-info'} style={{ margin: '8px 0', fontSize: 13 }}>
          {notice.text}
        </div>
      )}

      {phase === 'ready' && status && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px', alignItems: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>
            <span>Deployed: <code data-testid="demo-deployed-sha">{deployed ? short(deployed.sha) : 'not deployed'}</code>{deployed?.built_at ? ` · built ${when(deployed.built_at)}` : ''}</span>
            {componentVersions.length > 0 && (
              <span data-testid="demo-component-versions">
                {componentVersions.map((c, i) => (
                  <React.Fragment key={c.id}>{i > 0 ? ' · ' : ''}{c.label} <code>{short(c.version?.sha ?? null)}</code></React.Fragment>
                ))}
              </span>
            )}
            <span>Latest master: <code data-testid="demo-latest-sha">{short(status.latest_master_sha)}</code></span>
            {status.latest_master_sha && (
              <span className={upToDate ? 'badge badge-success' : 'badge badge-warning'}>{upToDate ? 'Up to date' : 'Update available'}</span>
            )}
          </div>
          {status.github_error && (
            <div className="alert alert-error" style={{ margin: '8px 0', fontSize: 13 }}>
              GitHub is unavailable: {ERROR_TEXT[status.github_error] ?? status.github_error}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', margin: '12px 0' }}>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || active || waiting} onClick={() => setConfirm({ kind: 'deploy' })}>
              {active || waiting ? 'Deploying…' : 'Deploy'}
            </button>
            {lastRun && (
              <span data-testid="demo-last-run" style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', gap: 8, alignItems: 'center' }}>
                Last run <RunBadge run={lastRun} /> {when(lastRun.created_at)}
                {lastRun.html_url && <a href={lastRun.html_url} target="_blank" rel="noopener noreferrer">View on GitHub</a>}
              </span>
            )}
          </div>
          <h4 style={{ margin: '12px 0 6px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>History</h4>
          {history.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No deployments yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table data-testid="demo-publish-history" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                    <th style={{ padding: '6px 4px' }}>When</th><th style={{ padding: '6px 4px' }}>Result</th>
                    <th style={{ padding: '6px 4px' }}>Commit</th><th style={{ padding: '6px 4px' }} />
                  </tr>
                </thead>
                <tbody>
                  {history.map((run) => {
                    const canRollBack = run.conclusion === 'success' && !!run.head_sha && run.head_sha !== deployed?.sha && !active && !waiting
                      && run.rollback_available !== false; // WQ-348: both images must exist
                    return (
                      <tr key={run.id} style={{ borderTop: '1px solid var(--border-secondary, rgba(148,163,184,0.15))' }}>
                        <td style={{ padding: '6px 4px' }}>{when(run.created_at)}</td>
                        <td style={{ padding: '6px 4px' }}><RunBadge run={run} /></td>
                        <td style={{ padding: '6px 4px' }}><code>{short(run.head_sha)}</code></td>
                        <td style={{ padding: '6px 4px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {run.html_url && <a href={run.html_url} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>Run</a>}
                          {canRollBack && (
                            <button type="button" className="btn btn-ghost btn-sm" disabled={busy}
                              onClick={() => setConfirm({ kind: 'rollback', sha: run.head_sha as string })}
                              aria-label={`Roll back to ${short(run.head_sha)}`}>
                              Roll back
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {isSuperAdmin && (phase === 'ready' || phase === 'unconfigured') && publishers !== null && (
        <Publishers publishers={publishers} onAdd={onAddPublisher} onRemove={onRemovePublisher} busy={busy} />
      )}

      {confirm?.kind === 'deploy' && (
        <ConfirmDialog
          title="Deploy the Demo Center?"
          body={`This builds the latest master (${short(status?.latest_master_sha)}) and replaces the demo content at icos.iwtwireless.com/demos. Only the demos container is restarted; the platform is not affected.`}
          confirmLabel="Deploy"
          onCancel={() => setConfirm(null)}
          onConfirm={onConfirm}
        />
      )}
      {confirm?.kind === 'rollback' && (
        <ConfirmDialog
          title={`Roll back to ${short(confirm.sha)}?`}
          body="This redeploys that earlier version of the demo content. Only the demos container is restarted; the platform is not affected."
          confirmLabel="Roll back"
          onCancel={() => setConfirm(null)}
          onConfirm={onConfirm}
        />
      )}
    </section>
  );
};

export default DemoPublishCard;
